import React, { Fragment, useMemo, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUp from '@mui/icons-material/KeyboardArrowUp';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  FormControl,
  IconButton,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import Link from '@mui/material/Link';

import { useBusinessDashboard } from '~/hooks/apis/business-traveller/useBusinessDashboard';
import { useSocieteOverviewMetadata } from '~/hooks/apis/business-traveller/useSocieteOverviewMetadata';

import { SocieteEnrolmentFilter } from '~/utils/businessTraveller/societeFilters';
import {
  BusinessActivityFilter,
  SocieteCohort,
  TIER_GROUPS,
  matchesBusinessActivity,
  matchesOverviewFilters,
  matchesSocieteCohort,
  summariseSociete,
  tierGroup,
} from '~/utils/businessTraveller/societeOverview';

import BusinessDashboardEmployees from './BusinessDashboardEmployees';
import BusinessDashboardOverview from './BusinessDashboardOverview';

const tiers: Array<App.LuxLoyaltyTier> = ['bronze', 'silver', 'gold', 'platinum', 'infinite'];
const money = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD', maximumFractionDigits: 0 });

export default function BusinessDashboard() {
  const queryClient = useQueryClient();
  const { data, isLoading, error, isFetching } = useBusinessDashboard(true);
  const [search, setSearch] = useState('');
  const [selectedBusinessId, setSelectedBusinessId] = useState<string | null>(null);
  const [statuses, setStatuses] = useState<Array<App.LuxLoyaltyTier>>([]);
  const [enrolment, setEnrolment] = useState<SocieteEnrolmentFilter>('all');
  const [activity, setActivity] = useState<BusinessActivityFilter>('12-months');
  const [cohort, setCohort] = useState<SocieteCohort | null>(null);
  const [page, setPage] = useState(0);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const overviewAvailable = data?.overviewVersion === 1;
  const filters = useMemo(() => ({ statuses, enrolment }), [statuses, enrolment]);
  const scopedBusinesses = useMemo(
    () =>
      (data?.businesses ?? []).filter(
        (business) =>
          matchesBusinessActivity(business, overviewAvailable ? activity : 'all') &&
          (selectedBusinessId
            ? business.id === selectedBusinessId
            : `${business.name} ${business.id}`.toLowerCase().includes(search.toLowerCase())),
      ),
    [data, search, selectedBusinessId, activity, overviewAvailable],
  );
  const metadataQueries = useSocieteOverviewMetadata(
    scopedBusinesses.filter((business) => business.employees.length > 0),
    overviewAvailable,
  );
  const metadata = new Map(
    metadataQueries.flatMap(({ businessId, query }) => (query.data ? [[businessId, query.data] as const] : [])),
  );
  const metadataReady = metadataQueries.every(({ query }) => query.isSuccess);
  const metadataFailed = metadataQueries.some(({ query }) => query.isError);
  const summary = summariseSociete(scopedBusinesses, filters, metadata);
  const employeeFiltersActive = statuses.length > 0 || enrolment !== 'all' || !!cohort;
  const matchingEmployees = new Map(
    scopedBusinesses.map((business) => [
      business.id,
      business.employees.filter(
        (employee) =>
          matchesOverviewFilters(employee, filters) &&
          (!cohort || matchesSocieteCohort(employee, cohort, metadata.get(business.id))),
      ),
    ]),
  );
  const businesses = scopedBusinesses.filter(
    (business) => !overviewAvailable || !employeeFiltersActive || matchingEmployees.get(business.id).length > 0,
  );
  const visiblePage = Math.min(page, Math.max(0, Math.ceil(businesses.length / 25) - 1));
  function selectCohort(value: SocieteCohort | null) {
    setCohort(value);
    setPage(0);
  }
  function toggle(id: string) {
    setExpanded((previous) => {
      const next = new Set(previous);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }
  return (
    <Stack gap={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Box>
          <Typography variant="h5">Société status by business</Typography>
          <Typography color="text.secondary">
            View employee status, Silver grants and progress towards qualification.
          </Typography>
        </Box>
        <Stack direction="row" gap={1} alignItems="center">
          {cohort && <Button onClick={() => selectCohort(null)}>Clear tile filter</Button>}
          <Button
            onClick={() => queryClient.invalidateQueries({ queryKey: ['business-traveller'] })}
            disabled={isFetching}
          >
            Reload
          </Button>
        </Stack>
      </Stack>
      <Stack direction={{ xs: 'column', md: 'row' }} gap={2}>
        <Autocomplete
          freeSolo
          openOnFocus
          forcePopupIcon
          size="small"
          sx={{ flex: 1 }}
          options={scopedBusinesses}
          value={data?.businesses.find((business) => business.id === selectedBusinessId) ?? null}
          inputValue={search}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.name)}
          getOptionKey={(option) => (typeof option === 'string' ? option : option.id)}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          filterOptions={(options, { inputValue }) =>
            options.filter((business) =>
              `${business.name} ${business.id}`.toLowerCase().includes(inputValue.toLowerCase()),
            )
          }
          onInputChange={(_, value, reason) => {
            setSearch(value);
            if (reason === 'input' || reason === 'clear') setSelectedBusinessId(null);
            setPage(0);
          }}
          onChange={(_, value) => {
            setSelectedBusinessId(typeof value === 'string' ? null : (value?.id ?? null));
            setSearch(typeof value === 'string' ? value : (value?.name ?? ''));
            setPage(0);
          }}
          renderOption={({ key, ...optionProps }, business) => (
            <Box component="li" key={key} {...optionProps}>
              <Stack>
                <Typography>{business.name}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {business.id}
                </Typography>
              </Stack>
            </Box>
          )}
          renderInput={(params) => <TextField {...params} label="Search business name or ID" />}
        />
        <FormControl size="small" sx={{ width: { xs: '100%', md: 200 } }}>
          <InputLabel id="employee-status-filter-label" shrink>
            Current status
          </InputLabel>
          <Select<Array<App.LuxLoyaltyTier>>
            labelId="employee-status-filter-label"
            label="Current status"
            multiple
            displayEmpty
            notched
            value={statuses}
            onChange={(event) => {
              const value = event.target.value;
              setStatuses(typeof value === 'string' ? (value.split(',') as Array<App.LuxLoyaltyTier>) : value);
              setPage(0);
            }}
            renderValue={(value) =>
              value.length ? value.map((tier) => tier[0].toUpperCase() + tier.slice(1)).join(', ') : 'All statuses'
            }
          >
            {tiers.map((tier) => (
              <MenuItem key={tier} value={tier}>
                <Checkbox checked={statuses.includes(tier)} />
                <ListItemText primary={tier[0].toUpperCase() + tier.slice(1)} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          select
          size="small"
          label="Société enrolment"
          value={enrolment}
          onChange={(event) => {
            setEnrolment(event.target.value as SocieteEnrolmentFilter);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', md: 200 } }}
        >
          <MenuItem value="all">All employees</MenuItem>
          <MenuItem value="enrolled">Enrolled</MenuItem>
          <MenuItem value="not-enrolled">Not enrolled</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Last booking"
          value={overviewAvailable ? activity : 'all'}
          disabled={!overviewAvailable}
          onChange={(event) => {
            setActivity(event.target.value as BusinessActivityFilter);
            setPage(0);
          }}
          sx={{ width: { xs: '100%', md: 180 } }}
        >
          <MenuItem value="12-months">Last 12 months</MenuItem>
          <MenuItem value="6-months">Last 6 months</MenuItem>
          <MenuItem value="90-days">Last 90 days</MenuItem>
          <MenuItem value="all">All time · includes never booked</MenuItem>
        </TextField>
      </Stack>
      {data && !overviewAvailable && (
        <Alert severity="info">
          Overview and booking activity filters will be available after the reporting update is deployed.
        </Alert>
      )}
      {data && (
        <BusinessDashboardOverview
          summary={summary}
          available={overviewAvailable}
          metadataReady={metadataReady}
          metadataFailed={metadataFailed}
          selected={cohort}
          onSelect={selectCohort}
        />
      )}
      {isLoading && <CircularProgress />}
      {error && <Alert severity="error">{error.message}</Alert>}
      {data && (
        <Paper variant="outlined">
          <TableContainer>
            <Table aria-label="Business lifetime performance">
              <TableHead>
                <TableRow>
                  <TableCell />
                  <TableCell>Business name</TableCell>
                  {[
                    'TTV (AUD)',
                    'Margin (AUD)',
                    'Orders',
                    employeeFiltersActive && overviewAvailable ? 'Employees (matching / total)' : 'Employee count',
                    ...TIER_GROUPS.map((group) => group.label),
                  ].map((label) => (
                    <TableCell key={label} align="right" sx={{ textTransform: 'capitalize', whiteSpace: 'nowrap' }}>
                      {label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {businesses.slice(visiblePage * 25, visiblePage * 25 + 25).map((business) => (
                  <Fragment key={business.id}>
                    <TableRow hover>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => toggle(business.id)}
                          aria-expanded={expanded.has(business.id)}
                          aria-label={`Expand ${business.name}`}
                        >
                          {expanded.has(business.id) ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ minWidth: 260 }}>
                        <Typography fontWeight={600}>{business.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          <Link
                            href={`/lebusiness/${encodeURIComponent(business.id)}/business`}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {business.id}
                          </Link>
                        </Typography>
                      </TableCell>
                      <TableCell align="right">{business.ttv === null ? '—' : money.format(business.ttv)}</TableCell>
                      <TableCell align="right">
                        {business.margin === null ? '—' : money.format(business.margin)}
                      </TableCell>
                      <TableCell align="right">{business.orders?.toLocaleString() ?? '—'}</TableCell>
                      <TableCell align="right">
                        {business.snapshotAvailable
                          ? employeeFiltersActive && overviewAvailable
                            ? `${matchingEmployees.get(business.id).length} / ${business.employees.length}`
                            : business.employees.length
                          : '—'}
                      </TableCell>
                      {TIER_GROUPS.map((group) => (
                        <TableCell key={group.key} align="right">
                          {business.snapshotAvailable
                            ? (overviewAvailable ? matchingEmployees.get(business.id) : business.employees).filter(
                                (employee) => tierGroup(employee.tier, employee.societe_enrolment) === group.key,
                              ).length
                            : '—'}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell colSpan={10} sx={{ p: 0, border: expanded.has(business.id) ? undefined : 0 }}>
                        <Collapse in={expanded.has(business.id)} unmountOnExit>
                          {expanded.has(business.id) && (
                            <BusinessDashboardEmployees
                              key={`${business.id}-${statuses.join(',')}-${enrolment}-${cohort}`}
                              business={business}
                              filters={filters}
                              employeeIds={
                                overviewAvailable && employeeFiltersActive
                                  ? matchingEmployees.get(business.id).map((employee) => employee.id)
                                  : undefined
                              }
                            />
                          )}
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </Fragment>
                ))}
                {!businesses.length && (
                  <TableRow>
                    <TableCell colSpan={10}>No businesses match the selected filters.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={businesses.length}
            page={visiblePage}
            rowsPerPage={25}
            rowsPerPageOptions={[25]}
            onPageChange={(_, next) => setPage(next)}
          />
        </Paper>
      )}
    </Stack>
  );
}
